import React, { Fragment } from 'react'
import styled from 'styled-components'
import {
  EditorState,
  RichUtils,
  convertToRaw,
  convertFromRaw,
} from 'draft-js'
import Editor from 'draft-js-plugins-editor'
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js'
import createMarkdownShortcutsPlugin from 'draft-js-markdown-shortcuts-plugin'
import createLinkifyPlugin from 'draft-js-linkify-plugin'
import blockedCommands from './blockedCommands'
import createToolbarWithPlugin from './createToolbarWithPlugin' // use the named toolbarPlugin import to get a plugin matched to a component instance
import EditorTitle from './EditorTitle'
import TopicTitleInput from './TopicTitleInput'
import EditorFooter from './EditorFooter'
import Files from './Files'
import LinkModal from './LinkModal'
import Modal from '../../../common/Modal'
import 'draft-js/dist/Draft.css' // Formatting will not work correctly without this
import NotesUtil from '../../helpers/NotesUtil'

const Container = styled.div`
  border-color: ${props => props.theme.border};
  border-style: solid;
  border-top-width: 0px;
  border-left-width: 0.5px;
  border-right-width: 0.5px;
  border-bottom-width:  0.5px;
  width: 100%; /* account for .5px borders on left and right */
  background-color: ${props => props.theme.white};
  resize: vertical;
  position: relative;
  min-height: 207px;
  overflow: hidden;
  padding-bottom: 10px;
  display: flex;
  flex-direction: column;
  color: ${props => props.theme.text};
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  line-height: 19px;
  box-sizing: border-box;
  .newParagraph {
    margin-bottom: 12px;
  }
`
const EditorContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  overflow-y: scroll;
  cursor: text;
  padding: 10px 15px 10px 15px;
  caret-color: auto;
  height: 100%;
  min-height: 115px;
  ul {
    margin-left: 20px;
  }
`
const FileInput = styled.input`
  visibility: hidden;
  height: 0px;
`

export default class RichEditor extends React.Component {
  constructor(props) {
    super(props)
    const saved = this.loadMarkdown()

    this.state = {
      editorState: saved ? EditorState.createWithContent(saved) : EditorState.createEmpty(),
      isLinkModalOpen: false,
      topicName: props.topicName,
      deleteFiles: [],
      files: props.fileUrls,
      buttonsDisabled: props.content === null,
    }
    this.setupPlugins()
  }

  componentDidMount() {
    window.requestAnimationFrame(() => {
      this.editor.focus()
    })
  }

  setupPlugins = () => {
    const markdownShortcutsPlugin = createMarkdownShortcutsPlugin()
    const linkifyPlugin = createLinkifyPlugin({ target: '_blank' })
    const { ToolbarComponent, toolbarPlugin } = createToolbarWithPlugin()

    this.plugins = [
      markdownShortcutsPlugin,
      toolbarPlugin,
      linkifyPlugin
    ]
    this.ToolbarComponent = ToolbarComponent
  }

  toggleLinkModal = () => {
    this.setState(({ isLinkModalOpen }) => ({ isLinkModalOpen: !isLinkModalOpen }))
  }

  onChange = editorState => {
    this.setState({ editorState })
    this.saveMarkdown(editorState)
    this.changeButtonState(editorState)
  }

  saveMarkdown = editorState => {
    return draftToMarkdown(convertToRaw(editorState.getCurrentContent()), { preserveNewlines: true })
  }

  loadMarkdown = () => {
    return convertFromRaw(markdownToDraft(this.props.content))
  }

  changeButtonState(editorState) {
    const buttonsDisabled = this.checkButtonStatus()

    this.setState({ buttonsDisabled })
  }

  handleKeyCommand(command, editorState) {
    if (!blockedCommands[command]) {
      const newState = RichUtils.handleKeyCommand(editorState, command)

      if (newState) {
        this.onChange(newState)
        return 'handled'
      }
    }
    return 'not-handled'
  }

  onTab = (e) => {
    e.preventDefault()
    const editorState = RichUtils.onTab(e, this.state.editorState, 4)

    this.setState({ editorState })
  }

  onEditorClick = () => {
    if (this.editor) {
      this.editor.focus()
    }
  }

  updateEditorState = editorState => {
    this.setState({ editorState })
  }

  onFileUploadClick = () => {
    if (this.fileInput) {
      this.fileInput.click()
    }
  }

  onTopicTitleChange = event => {
    if (!this.props.predefined) {
      this.setState({
        ...this.state,
        topicName: event.target.value,
      })
    }
  }

  onFileChange = acceptedFiles => {
    const file = acceptedFiles.dataTransfer ? acceptedFiles.dataTransfer.files : acceptedFiles
    const { files } = this.state;

    files.push(file)
    this.setState({
      ...this.state,
      files
    })
    const buttonsDisabled = this.checkButtonStatus()
    this.setState({ buttonsDisabled })
  }

  getDeleteFilesUUID= filename => {
    const { fileUUIDs } = this.props

    return fileUUIDs.filter(({ name }) => name === filename).map(({ uuid }) => uuid)
  }

  onFileDeleteClick = filename => {
    const { editorState } = this.state
    const files = this.state.files.filter(file => file.name !== filename)
    const fileUUID = this.getDeleteFilesUUID(filename)

    if (fileUUID.length > 0) {
      this.state.deleteFiles.push(fileUUID[0])
    }
    const buttonsDisabled = !(files.length > 0 && draftToMarkdown(convertToRaw(editorState.getCurrentContent())).length > 0)
    this.setState({
      ...this.state,
      files,
      buttonsDisabled,
    })
  }

  getEditorContent = () => {
    const { editorState } = this.state

    return this.saveMarkdown( editorState )
  }

  onPostClick = () => {
    const { onPostClick } = this.props
    const { topicName, files, deleteFiles } = this.state

    onPostClick(topicName, this.getEditorContent(), files, deleteFiles);
  }

  onSaveAsDraftClick = () => {
    const { onSaveAsDraftClick } = this.props
    const { topicName, files, deleteFiles } = this.state

    onSaveAsDraftClick(topicName, this.getEditorContent(), files, deleteFiles);
  }

  checkButtonStatus = () => {
    const { topicName , files, editorState  } = this.state
    const { questionStatus, predefined } = this.props
    const questionFlow = questionStatus.includes('question') || questionStatus.includes('reply')

    if (!questionFlow) {
      if (predefined) {
        return !(files.length > 0 || draftToMarkdown(convertToRaw(editorState.getCurrentContent())).length > 0 )
      } else {
        return !((!!topicName && topicName.length > 0) || (files !== undefined && files.length > 0 && draftToMarkdown(convertToRaw(editorState.getCurrentContent())).length > 0 ))
      }
    } else {
      return !((files !== undefined && files.length > 0) || draftToMarkdown(convertToRaw(editorState.getCurrentContent())).length > 0)
    }
  }

  onCloseClick = () => {
    const { onCloseClick } = this.props
    const { topicName , editorState } = this.state
    const content = this.saveMarkdown(editorState)

    onCloseClick(topicName, content)
  }

  render() {
    const { ToolbarComponent } = this
    const { isEditDraft, questionStatus, published, predefined, isBankUser, onDeleteClick } = this.props
    const { editorState, isLinkModalOpen, buttonsDisabled, topicName, files } = this.state
    return (
      <Fragment>
        <EditorTitle
          title={ NotesUtil.getEditorTitle(questionStatus, isEditDraft) }
          onCloseClick={this.onCloseClick}
        />
        <Container>
          <EditorContainer>
            {!NotesUtil.hideEditorTitle(questionStatus) && (
              <TopicTitleInput
                placeholder="Type your topic here"
                value={topicName || ''}
                predefined={predefined}
                onChange={this.onTopicTitleChange}
                />
            )}
            <Editor
              onClick={this.onEditorClick}
              handleKeyCommand={this.handleKeyCommand}
              editorState={editorState}
              onChange={this.onChange}
              autoCapitalize="on"
              plugins={this.plugins}
              ref={el => this.editor = el}
              placeholder={ NotesUtil.getPlaceholderText(questionStatus) }
              blockStyleFn={NotesUtil.customBlockStyleFn}
            />
          </EditorContainer>
          <FileInput
            type="file"
            multiple accept="application/pdf, image/*"
            innerRef={el => this.fileInput = el}
            onChange={e => this.onFileChange(e.target.files[0])} />
          <Files
            published={false}
            files={files}
            onFileDeleteClick={this.onFileDeleteClick}
            hideClose={false}
          />
          <EditorFooter
            disabled={buttonsDisabled}
            published={published}
            predefined={predefined}
            isBankUser={isBankUser}
            questionStatus={questionStatus}
            onPostClick={this.onPostClick}
            onSaveAsDraftClick={this.onSaveAsDraftClick}
            onLinkButtonClick={this.toggleLinkModal}
            onFileUploadClick={this.onFileUploadClick}
            onDeleteClick={onDeleteClick}
            ToolbarComponent={ToolbarComponent} />
        </Container>
        <Modal isModalOpen={isLinkModalOpen}>
          <LinkModal
            toggleModal={this.toggleLinkModal}
            editorState={editorState}
            updateEditorState={this.updateEditorState}
          />
        </Modal>
      </Fragment>
    )
  }
}
